import { useEffect, useRef } from 'react'

import { toast } from '@ds/components'
import { useI18n } from '@muse/i18n'

import { buildParams, sendEvent } from '@core/analytics'
import { HttpStatusCode } from '@core/http-client'
import { downloadFileByUrl } from '@features/audio-download'
import { useAuthModal } from '@features/auth'
import { useCurrentUser } from '@features/session'

import {
    CollectionDownloadTimeoutError,
    collectionDownloadAborted,
    isCollectionDownloadAborted,
} from '../helpers/collectionDownloadErrors'
import { describeCollectionDownloadFailure } from '../helpers/describeCollectionDownloadFailure'
import { getCollectionDownloadUrl } from '../services/getCollectionDowloadUrl'

export type TDownloadCollection = {
    id: string
    title: string
}

const ZIP_POLL_DELAY_MS = 2000

const PREPARATION_TIMEOUT_MS = 180_000

const wait = (ms: number) =>
    new Promise(resolve => {
        setTimeout(resolve, ms)
    })

/**
 * 202 carries no url yet. Taking the first response at face value hands the caller `undefined`, and
 * an anchor with an undefined href navigates the page to a relative `undefined` instead.
 */
const resolveDownloadUrl = async (
    collectionId: string,
    signal: AbortSignal,
    deadline: number,
): Promise<string> => {
    const response = await getCollectionDownloadUrl({ collectionId, signal })
    const { url } = await response.json<{ url: string }>()

    if (response.status === HttpStatusCode.OK) {
        return url
    }

    if (response.status !== HttpStatusCode.ACCEPTED) {
        throw new Error('Failed to get collection download url')
    }

    // Checked before the sleep, or the last round waits out its two seconds past the deadline.
    if (Date.now() + ZIP_POLL_DELAY_MS >= deadline) {
        throw new CollectionDownloadTimeoutError()
    }

    await wait(ZIP_POLL_DELAY_MS)

    if (signal.aborted) {
        throw collectionDownloadAborted()
    }

    return resolveDownloadUrl(collectionId, signal, deadline)
}

export const useCollectionDownload = () => {
    const { t } = useI18n()
    const { open: openAuthModal } = useAuthModal()
    const { id: userId } = useCurrentUser()
    const abortRef = useRef<AbortController | null>(null)

    // Not on unmount: the zip takes minutes, the screen this is called from unmounts on the first
    // soft navigation, and aborting there would drop a download the visitor is still waiting for.
    useEffect(() => {
        const abort = () => abortRef.current?.abort()

        window.addEventListener('pagehide', abort)

        return () => window.removeEventListener('pagehide', abort)
    }, [])

    const download = async (collection: TDownloadCollection) => {
        sendEvent({ name: 'collection_download_click', payload: { collection: collection.id } })

        if (userId === 0) {
            openAuthModal({
                greetingKey: 'download',
                intent: { kind: 'download-collection', collection },
            })

            return
        }

        abortRef.current?.abort()

        const controller = new AbortController()

        abortRef.current = controller

        const preparing = toast(t('i18n.collectionDownloadPreparing'), { icon: '⬇️' })

        try {
            const url = await resolveDownloadUrl(
                collection.id,
                controller.signal,
                Date.now() + PREPARATION_TIMEOUT_MS,
            )

            downloadFileByUrl(url, collection.title, true)

            sendEvent({
                name: 'collection_download_success',
                payload: { collection: collection.id },
            })
        } catch (error) {
            // Leaving the page is not a failure, and the user is no longer there to be told.
            if (isCollectionDownloadAborted(error)) {
                return
            }

            const { reason, status, messageKey } = describeCollectionDownloadFailure(error)

            sendEvent({
                name: 'collection_download_unsuccess',
                payload: { collection: collection.id, ...buildParams({ reason, status }) },
            })

            toast.error(t(messageKey))
        } finally {
            toast.dismiss(preparing)
        }
    }

    return { download }
}
